/**
 * Small pieces the partner screens share: a status chip from a label map, a
 * failed read with the server's words, and a dialog that asks for a reason
 * before a consequential action (suspend, reject, fail a payout…).
 */

import { useState, type ReactNode } from 'react';
import {
  Alert,
  Button,
  ErrorState,
  Modal,
  StatusChip,
  Textarea,
  type ButtonVariant,
  type Tone,
} from '@connect/web-shared';

import { refusalText } from '../messaging/api-refusal';
import { labelsOf } from './api';

export function Chip({ value, map }: { value: string; map: Record<string, { label: string; tone: Tone }> }) {
  return <StatusChip value={value} {...labelsOf(map)} />;
}

export function Failed({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return <ErrorState message={refusalText(error)} onRetry={onRetry} />;
}

export function errorText(error: unknown): string {
  return refusalText(error) ?? 'Something went wrong. Try again.';
}

export function ReasonDialog({
  open,
  title,
  description,
  confirmLabel,
  variant = 'primary',
  minLength = 3,
  busy,
  error,
  onClose,
  onConfirm,
  children,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  variant?: ButtonVariant;
  minLength?: number;
  busy?: boolean;
  error?: unknown;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  children?: ReactNode;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={variant}
            loading={busy}
            disabled={reason.trim().length < minLength}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? <Alert tone="danger">{errorText(error)}</Alert> : null}
        {children}
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </div>
    </Modal>
  );
}
